import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import makeStyles from "@mui/styles/makeStyles";

import Container from "@mui/material/Container";

import { diffObject, notBlank, isBlank } from "../util/ext";
import Grid from "@mui/material/Grid";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import PanelLoadingIndicator from "../components/PanelLoadingIndicator";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Header from "../creator-application/common/Header";
import { useProject, useProjectUpdate } from "./cspServices";
import ProjectLogoCard from "./cards/ProjectLogoCard";
import ProjectDetailsCard from "./cards/ProjectDetailsCard";
import ProjectEmailCard from "./cards/ProjectEmailCard";
import CustomFieldsCard from "./cards/CustomFieldsCard";
import RipEndpointCard from "./cards/RipEndpointCard";
import DefaultModulesCard from "./cards/DefaultModulesCard";
import SubmissionTypesCard from "./cards/SubmissionTypesCard";
import ContributorRolesCard from "./cards/ContributorRolesCard";
import { lensProp, map, propEq, set } from "ramda";
import MediumFormatsCard from "./cards/MediumFormatsCard";
import AudioTypesCard from "./cards/AudioTypesCard";
import ProjectCategoriesCard from "./cards/ProjectCategoriesCard";
import ProjectOptInsCard from "./cards/ProjectOptInsCard";
import LibraryOptionsCard from "./cards/LibraryOptionsCard";
import LandingPageCard from "./cards/LandingPageCard";
import OrganizationLogosCard from "./cards/OrganizationLogosCard";
import { caseInsensitiveSort } from "../util/sorts";
import Typography from "@mui/material/Typography";
import ErrorMessage from "../components/alert/ErrorMessage";
import { useConfirm } from "../common/use-confirm";
import ConfirmDialog from "../components/ConfirmDialog";
import { AlertContext, AlertSeverity } from "../components/alert/AlertProvider";
import ProjectFieldOptionsCard from "./cards/ProjectFieldOptionsCard";
import CustomDropdownCard from "./cards/CustomDropdownCard";
import ProjectLanguagesCard from "./cards/ProjectLanguagesCard";
import { getProjectUrl } from "./cspUtils";

const useStyles = makeStyles(theme => ({
  root: {
    border: "3px white solid",
    backgroundColor: "white",
    borderRadius: "20px",
  },

  panel: {
    backgroundColor: "white",
    paddingTop: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    maxHeight: "calc(100vh - 300px)",
    overflowY: "auto",
  },
}));

const sortSubmissionTypes = caseInsensitiveSort("name");

const showMediumFormats = st =>
  st.name === "OTHER_DOCUMENT" ||
  st.name === "OTHER_DOCUMENT_WITH_DOCX" ||
  st.name === "OTHER_DOCUMENT_PDF_AND_DOCX" ||
  st.name === "OTHER_DOCUMENT_PDF_ONLY" ||
  st.name === "IMAGE";

const ProjectView = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const state = useMemo(() => location?.state ?? {}, [location]);
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [originalProject, setOriginalProject] = useState(null);

  //-- Manage the confirm dialog
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  const { alertError, showMessage } = useContext(AlertContext);

  const projectResponse = useProject(projectId);
  useEffect(() => {
    if (projectResponse.isSuccess && notBlank(projectResponse.data)) {
      console.log("Setting the project and the original");
      setProject(projectResponse.data);
      setOriginalProject(projectResponse.data);
    }
  }, [projectResponse.isSuccess, projectResponse.data]);

  const handleUpdate = useCallback(update => {
    setProject(update);
  }, []);

  const handleImageUrlUpdate = field => url => {
    setProject(prev => set(lensProp(field), url, prev));
  };

  const projectUpdate = useProjectUpdate();
  const [waiting, setWaiting] = useState(false);
  const handleSaveProject = useCallback(async () => {
    setWaiting(true);
    const update = project.useDefaultCategories
      ? set(lensProp("categories"), [], project)
      : project;
    try {
      await projectUpdate.mutateAsync({
        resourceIds: { id: project.projectId },
        data: update,
      });
      showMessage("Project Successfully Saved.", AlertSeverity.success);
    } catch (err) {
      alertError(err);
    } finally {
      setWaiting(false);
    }
  }, [project, projectUpdate, alertError, showMessage]);

  const goBack = useCallback(() => {
    notBlank(state) && notBlank(state.previousLocation)
      ? navigate(state.previousLocation, {
          state: {
            label: "Back to All Programs",
            previousLocation: "/manage-csp/programs",
          },
        })
      : navigate(`/manage-csp/programs/${project.programId}`, {
          state: {
            label: "Back to All Programs",
            previousLocation: "/manage-csp/programs",
          },
        });
  }, [state, navigate, project]);

  const handleBackClicked = useCallback(() => {
    const diff = diffObject(originalProject, project);
    if (notBlank(diff)) {
      showConfirm(
        "Discard Changes?",
        "You have unsaved changes to this Project.  Do you wish to discard these changes?",
        {
          onConfirm: () => {
            goBack();
          },
        },
      );
    } else {
      goBack();
    }
  }, [originalProject, project, showConfirm, goBack]);

  const disableSave = useCallback(() => {
    return waiting || isBlank(diffObject(originalProject, project));
  }, [originalProject, project, waiting]);

  return (
    <Container id="project-view" classes={{ root: classes.root }}>
      {projectResponse.isLoading && (
        <Container style={{ height: 400 }}>
          <PanelLoadingIndicator />
        </Container>
      )}
      {projectResponse.isError && (
        <Box padding={5} fontSize="1.4rem" fontStyle="italic">
          <Typography variant="h2">Error Loading Project.</Typography>
          <ErrorMessage error={projectResponse.error} />
        </Box>
      )}

      {projectResponse.isSuccess && notBlank(project) && (
        <Fragment>
          <Header
            title={`Manage ${project.projectName}`}
            subtitle={
              getProjectUrl(project).startsWith("https://")
                ? null
                : getProjectUrl(project)
            }
            link={
              getProjectUrl(project).startsWith("https://")
                ? getProjectUrl(project)
                : null
            }
            selectedActions={
              <Box>
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBackClicked}
                >
                  {notBlank(state) && notBlank(state.label)
                    ? state.label
                    : "Back to Project"}
                </Button>
              </Box>
            }
          />
          <Container classes={{ root: classes.panel }}>
            <Grid container spacing={2}>
              <Grid item sm={12}>
                <Grid container spacing={2}>
                  <Grid item sm={3} container spacing={2}>
                    <Grid item sm={12}>
                      <ProjectLogoCard
                        project={project}
                        url={project.logoUri}
                        onUrlUpdate={handleImageUrlUpdate("logoUri")}
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <ProjectLogoCard
                        project={project}
                        url={project.logoWhiteUri}
                        title="White Logo"
                        darkBackground
                        onUrlUpdate={handleImageUrlUpdate("logoWhiteUri")}
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <ProjectLogoCard
                        project={project}
                        url={project.landingPageImageUri}
                        title="Project Image"
                        onUrlUpdate={handleImageUrlUpdate(
                          "landingPageImageUri",
                        )}
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <ProjectLogoCard
                        project={project}
                        url={project.faviconImageUri}
                        title="Favicon"
                        onUrlUpdate={handleImageUrlUpdate("faviconImageUri")}
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <CustomFieldsCard
                        loading={projectResponse.isLoading}
                        project={project}
                        onUpdate={handleUpdate}
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <CustomDropdownCard
                        title="Custom Dropdown 1"
                        fieldOptionProp="sub_projects"
                        dropdownItemsProp="subProjects"
                        dropdownItemNameProp="subProjectName"
                        dropdownItemIdProp="subProjectId"
                        fieldNames={{
                          label: "subProjectsLabel",
                          required: "subProjectsRequired",
                        }}
                        loading={projectResponse.isLoading}
                        project={project}
                        onUpdate={handleUpdate}
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <CustomDropdownCard
                        title="Custom Dropdown 2"
                        fieldOptionProp="custom_dropdown"
                        dropdownItemsProp="customDropdownItems"
                        dropdownItemNameProp="customDropdownName"
                        dropdownItemIdProp="customDropdownIdProp"
                        fieldNames={{
                          label: "customDropdownLabel",
                          required: "customDropdownRequired",
                        }}
                        loading={projectResponse.isLoading}
                        project={project}
                        onUpdate={handleUpdate}
                      />
                    </Grid>
                  </Grid>

                  <Grid item sm={9} container spacing={2}>
                    <Grid item sm={12}>
                      <ProjectDetailsCard
                        loading={projectResponse.isLoading}
                        project={project}
                        onUpdate={handleUpdate}
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <LandingPageCard
                        loading={projectResponse.isLoading}
                        project={project}
                        onUpdate={handleUpdate}
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <ProjectEmailCard
                        loading={projectResponse.isLoading}
                        project={project}
                        onUpdate={handleUpdate}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item sm={12}>
                <Grid container spacing={2}>
                  <Grid item sm={3} container spacing={2}>
                    <Grid item sm={12}>
                      <RipEndpointCard
                        loading={projectResponse.isLoading}
                        project={project}
                        onUpdate={handleUpdate}
                      />
                    </Grid>
                    <Grid item sm={12}>
                      <DefaultModulesCard
                        loading={projectResponse.isLoading}
                        project={project}
                        onUpdate={handleUpdate}
                      />
                    </Grid>
                  </Grid>

                  <Grid item sm={9} container spacing={2}>
                    <Grid item sm={12}>
                      <ProjectOptInsCard
                        loading={projectResponse.isLoading}
                        project={project}
                        onUpdate={handleUpdate}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item sm={12}>
                <Grid container spacing={2}>
                  <Grid item sm={4} container spacing={2}>
                    <Grid item sm={12}>
                      <SubmissionTypesCard
                        loading={projectResponse.isLoading}
                        project={project}
                        onUpdate={handleUpdate}
                      />
                    </Grid>

                    <Grid item sm={12}>
                      <ProjectLanguagesCard
                        project={project}
                        onUpdate={handleUpdate}
                        loading={projectResponse.isLoading}
                      />
                    </Grid>

                    <Grid item sm={12}>
                      <Grid container spacing={2}>
                        {map(
                          st => (
                            <Fragment key={st.name}>
                              <Grid item sm={6}>
                                <ContributorRolesCard
                                  loading={projectResponse.isLoading}
                                  project={project}
                                  submissionType={st}
                                  onUpdate={handleUpdate}
                                />
                              </Grid>
                              {showMediumFormats(st) && (
                                <Grid item sm={6}>
                                  <MediumFormatsCard
                                    loading={projectResponse.isLoading}
                                    project={project}
                                    submissionType={st}
                                    onUpdate={handleUpdate}
                                  />
                                </Grid>
                              )}
                              {propEq("name", "AUDIO", st) && (
                                <Grid item sm={6}>
                                  <AudioTypesCard
                                    loading={projectResponse.isLoading}
                                    project={project}
                                    submissionType={st}
                                    onUpdate={handleUpdate}
                                  />
                                </Grid>
                              )}
                            </Fragment>
                          ),
                          sortSubmissionTypes(project.submissionTypes),
                        )}
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item sm={8}>
                    <Grid container spacing={2}>
                      <Grid item sm={6}>
                        <ProjectCategoriesCard
                          loading={projectResponse.isLoading}
                          project={project}
                          onUpdate={handleUpdate}
                        />
                      </Grid>

                      <Grid item sm={6}>
                        <LibraryOptionsCard
                          loading={projectResponse.isLoading}
                          project={project}
                          onUpdate={handleUpdate}
                        />
                      </Grid>

                      <Grid item sm={12}>
                        <ProjectFieldOptionsCard
                          project={project}
                          onUpdate={handleUpdate}
                        />
                      </Grid>

                      <Grid item sm={12}>
                        <OrganizationLogosCard project={project} />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Container>
          <Box padding={4} display="flex" justifyContent="flex-end">
            <Button
              onClick={handleSaveProject}
              disabled={disableSave()}
              color="primary"
              variant="contained"
            >
              Save
            </Button>
          </Box>
        </Fragment>
      )}
      <ConfirmDialog handler={confirmHandler} />
    </Container>
  );
};

export default ProjectView;
